// HoldToConfirm — a destructive control that fires only after a deliberate,
// sustained press, with the progress visible while you hold.
//
// Why not window.confirm or a click-twice toggle: a modal is a different question
// in a different place ("are you sure?" — sure about what?), and click-twice is
// two identical gestures where the second is indistinguishable from a double-click.
// A timed hold cannot be produced by a stray click at all, and the fill tells you
// exactly how committed you are while there is still time to back out.
//
// Release early and nothing happens — no state written, no callback fired.
//
// Test seam: `window.__HOLD_TO_CONFIRM__` records the last completed confirm id,
// so an e2e can assert the action fired without depending on its side effects.

import React, { useCallback, useEffect, useRef, useState } from 'react'

export const DEFAULT_HOLD_MS = 800

/**
 * Props:
 *   onConfirm()   — fired once, only on a completed hold.
 *   holdMs        — hold duration; defaults to DEFAULT_HOLD_MS.
 *   title         — tooltip / accessible label.
 *   testId        — data-testid for the button.
 *   confirmId     — recorded on window.__HOLD_TO_CONFIRM__ when it fires.
 *   children      — the resting content (e.g. an ✕ glyph).
 *   style         — merged over the button's own styles.
 */
export default function HoldToConfirm({
  onConfirm,
  holdMs = DEFAULT_HOLD_MS,
  title,
  testId,
  confirmId,
  children,
  style,
}) {
  const [progress, setProgress] = useState(0)   // 0..1
  const rafRef = useRef(null)
  const startRef = useRef(0)
  const firedRef = useRef(false)

  const stop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    setProgress(0)
  }, [])

  // Cancel on unmount so a hold interrupted by a re-render can't fire late.
  useEffect(() => stop, [stop])

  const tick = useCallback((nowMs) => {
    const elapsed = nowMs - startRef.current
    const p = Math.min(1, elapsed / holdMs)
    setProgress(p)
    if (p < 1) {
      rafRef.current = requestAnimationFrame(tick)
      return
    }
    // Complete. Guard against a double-fire if a stray frame lands after this.
    if (firedRef.current) return
    firedRef.current = true
    rafRef.current = null
    setProgress(0)
    if (typeof window !== 'undefined' && confirmId) {
      window.__HOLD_TO_CONFIRM__ = confirmId
    }
    onConfirm?.()
  }, [holdMs, onConfirm, confirmId])

  const begin = useCallback((e) => {
    // Don't let the press reach the card underneath — this control sits on top of
    // a card whose whole job is "open this character".
    e.stopPropagation()
    e.preventDefault()
    if (rafRef.current != null) return
    firedRef.current = false
    startRef.current = performance.now()
    rafRef.current = requestAnimationFrame(tick)
  }, [tick])

  const cancel = useCallback((e) => {
    e?.stopPropagation()
    stop()
  }, [stop])

  const held = progress > 0

  return (
    <button
      type="button"
      data-testid={testId}
      data-hold-progress={progress.toFixed(2)}
      title={title}
      aria-label={title}
      onPointerDown={begin}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      // A plain click must never be enough — swallow it so nothing downstream
      // treats this as an ordinary activation.
      onClick={(e) => { e.stopPropagation(); e.preventDefault() }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.preventDefault() }}
      style={{
        all: 'unset',
        position: 'relative',
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        overflow: 'hidden',
        color: held ? 'var(--m-danger)' : 'var(--m-fg-3)',
        border: `1px solid ${held
          ? 'color-mix(in srgb, var(--m-danger) 55%, transparent)'
          : 'color-mix(in srgb, var(--m-fg) 14%, transparent)'}`,
        background: held
          ? 'color-mix(in srgb, var(--m-danger) 12%, transparent)'
          : 'transparent',
        transition: 'color .12s, border-color .12s',
        ...style,
      }}
    >
      {/* The fill. A left-to-right bar rather than a spinner: it reads as "how far
          through" at 16px, where a radial arc does not. */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${progress * 100}%`,
          background: 'color-mix(in srgb, var(--m-danger) 38%, transparent)',
          pointerEvents: 'none',
        }}
      />
      <span style={{ position: 'relative', lineHeight: 1 }}>{children}</span>
    </button>
  )
}
